import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '@pc/api/client';
import { Loading } from '../components/ui';
import { AccountActions } from '../components/accounts/AccountActions';
import { AccountSummary } from '../components/accounts/AccountSummary';
import { ResourcesPanel } from '../components/accounts/ResourcesPanel';
import { DeleteAccount } from '../components/accounts/DeleteAccount';
import { AccountsBrowser } from '../components/accounts/AccountsBrowser';
import { QueryFailure, type GetAccountResponse, type GetAccountResourcesResponse } from '../components/accounts/shared';

/**
 * Same pattern the contract uses for the `discordID` path parameter
 * (`^[1-9][0-9]{16,19}$`), checked client-side so a malformed id is caught
 * before a request is fired rather than surfacing as a 400 from the API.
 */
const DISCORD_ID_PATTERN = /^[1-9][0-9]{16,19}$/;

interface AccountsSearch {
  discordID?: string;
}

export const Route = createFileRoute('/accounts')({
  // Held in the URL rather than component state so a lookup is shareable —
  // an operator can hand someone `/accounts?discordID=...` directly. The key
  // is omitted rather than set to `undefined` because `exactOptionalPropertyTypes`
  // treats those as different things.
  validateSearch: (search: Record<string, unknown>): AccountsSearch => {
    const { discordID } = search;
    return typeof discordID === 'string' && discordID.length > 0 ? { discordID } : {};
  },
  component: AccountsScreen,
});

function AccountsScreen() {
  const { discordID } = Route.useSearch();

  const isValidId = discordID !== undefined && DISCORD_ID_PATTERN.test(discordID);

  return (
    <section>
      <h1>Accounts</h1>

      {/*
        * One way in, not two. The browser's search accepts a snowflake as well
        * as a cmpID, email or username, so a second Discord-ID-only box beside
        * it was strictly a subset — and two search fields on one screen is a
        * question the operator has to answer before they can start.
        */}
      {discordID === undefined && <AccountsBrowser />}

      {discordID !== undefined && (
        <p>
          <Link to="/accounts" search={{}}>
            ← All accounts
          </Link>
        </p>
      )}

      {discordID !== undefined && !isValidId && (
        <p role="alert">
          &ldquo;{discordID}&rdquo; is not a valid Discord snowflake. The contract requires 17 to 20 digits with
          no leading zero.
        </p>
      )}

      {isValidId && <AccountLookup key={discordID} discordID={discordID} />}
    </section>
  );
}


/**
 * The two reads are independent: `getAccountResources` can succeed while
 * `getAccount` 503s (or vice versa), so each gets its own query and its own
 * error surface rather than being merged into one request that fails as a
 * unit.
 */
/*
 * Remounted per account via a `key` at the call site, so mutation state — a
 * half-typed confirmation, a chosen tier — cannot survive into a lookup of a
 * different account. Keying the whole lookup rather than individual children
 * also keeps every child of this fragment unkeyed: mixing one keyed child in
 * among unkeyed conditional siblings made React leave a stale copy of it in
 * the DOM, which is how the actions panel came to render twice.
 */
function AccountLookup({ discordID }: { discordID: string }) {
  const account = useQuery({
    queryKey: ['accounts', discordID],
    queryFn: async (): Promise<GetAccountResponse> => {
      const { data } = await api.GET('/api/accounts/{discordID}', { params: { path: { discordID } } });
      return data as GetAccountResponse;
    },
  });

  const resources = useQuery({
    queryKey: ['accounts', discordID, 'resources'],
    queryFn: async (): Promise<GetAccountResourcesResponse> => {
      const { data } = await api.GET('/api/accounts/{discordID}/resources', {
        params: { path: { discordID } },
      });
      return data as GetAccountResourcesResponse;
    },
  });

  return (
    <>
      {account.isPending && <Loading what="account" />}
      {account.isError && <QueryFailure error={account.error} onRetry={() => void account.refetch()} />}
      {account.isSuccess && <AccountSummary account={account.data} />}

      {/*
        * The estate comes before the controls that change it. An operator opens
        * an account to find a container, and this screen's own rule — a
        * mutation is reached by scrolling past the state it changes — applies
        * to the fleet as much as to the record above it: locking an account
        * cascades to every container listed here.
        */}
      {resources.isPending && <Loading what="resources" />}
      {resources.isError && <QueryFailure error={resources.error} onRetry={() => void resources.refetch()} />}
      {resources.isSuccess && <ResourcesPanel resources={resources.data} />}

      {account.isSuccess && (
        /*
         * The directory status and `hostingDataFound` go down alongside the
         * hosting record's own fields because Activate is a reconciliation
         * between the two sides, not a flip of the active flag — an account can
         * be active while the directory read says `not-found`, and that drift is
         * the case the control exists for. See `AccountActions`.
         */
        <AccountActions
          discordID={discordID}
          currentTier={account.data.account?.tier ?? null}
          active={account.data.account?.active ?? null}
          directoryStatus={account.data.directory?.status}
          hostingDataFound={account.data.hostingDataFound}
        />
      )}

      {/* Last, after everything it destroys, rather than beside the routine
          lifecycle buttons it is nothing like.

          `hostingDataFound` rides along so the card can render an absence
          instead of an armed destructive control when the summary above has
          already said there is no hosting record here. It is `undefined` while
          the read is pending or after it failed, which withholds nothing — only
          a positive `false` does.

          `isError` rides along beside it because those two `undefined`s are not
          the same fact. A pending read must not withhold the control; a failed
          one must, and it is this query's failure that means it — the card is
          otherwise armed directly beneath the access-denied notice rendered a
          few lines up, offering to destroy an estate the console just failed to
          read. The resources query keeps its own surface and shapes only what
          the card says. */}
      <DeleteAccount
        discordID={discordID}
        hostingDataFound={account.data?.hostingDataFound}
        accountReadFailed={account.isError}
      />
    </>
  );
}
